import { ICryptoAddress } from "@novel/shared/interfaces/CryptoAddress";
import { ICryptoWallet } from "@novel/shared/interfaces/CryptoWallet";
import { ITokenCollectionClaimWithTokenCollection } from "@novel/shared/interfaces/TokenCollectionClaim";
import { normalizeBlockchainError } from "@novel/shared/utils/normalizeBlockchainError";

import { NovelGalleryThunkAction } from "@customer-gallery/redux/galleryTypedRedux";

import { isErrorSurfaceable } from "@novel/shared/utils/SurfaceableError";
import { GalleryMintingWallet } from "@customer-gallery/utils/galleryWallets";
import { dismissToast, loadingToast } from "@novel/shared/utils/toastUtils";

export const whitelistCollectionTokensForWallet =
    (
        cryptoAddress: ICryptoAddress,
        wallet: ICryptoWallet,
        tokenClaim: ITokenCollectionClaimWithTokenCollection,
    ): NovelGalleryThunkAction<
        | "LOADING_WHITELIST_TOKENS_FOR_WALLET"
        | "ERROR_LOADING_WHITELIST_TOKENS_FOR_WALLET"
        | "LOADED_WHITELIST_TOKENS_FOR_WALLET"
    > =>
    async (dispatch) => {
        const orderId = tokenClaim.claimOrder.id;
        const { collectionMetadata } = tokenClaim.collectionToMintFrom;
        const collectionContract = collectionMetadata.collectionSmartContract!;

        const { onSuccessToast } = loadingToast(
            `Whitelisting ${tokenClaim.quantity} token${
                tokenClaim.quantity > 1 ? "s" : ""
            } (this can take a minute)...`,
        );

        try {
            dispatch({
                type: "LOADING_WHITELIST_TOKENS_FOR_WALLET",
                payload: undefined,
            });

            await new GalleryMintingWallet(wallet).whitelistClaim({
                orderId: tokenClaim.claimOrder.id,
                claimId: tokenClaim.id,
                cryptoAddress: cryptoAddress.address,
                claimQuantity: tokenClaim.quantity,
                collectionSmartContract: collectionContract,
            });

            onSuccessToast(
                `${tokenClaim.quantity} Token${
                    tokenClaim.quantity > 1 ? "s" : ""
                } whitelisted for wallet!`,
            );

            return dispatch({
                type: "LOADED_WHITELIST_TOKENS_FOR_WALLET",
                payload: {
                    cryptoAddress,
                    orderId,
                    collectionContractAddress: collectionContract.address,
                    claimingTokenLineItem: tokenClaim,
                },
            });
        } catch (error: any) {
            return dispatchWhitelistError(error);
        }

        function dispatchWhitelistError(error: any): any {
            dismissToast();
            if (isErrorSurfaceable(error)) {
                dispatch({
                    type: "ERROR_LOADING_WHITELIST_TOKENS_FOR_WALLET",
                    payload: {
                        errorMessage: error.message,
                    },
                });

                throw error;
            }

            const { fullErrorMessage } = normalizeBlockchainError(error);
            dispatch({
                type: "ERROR_LOADING_WHITELIST_TOKENS_FOR_WALLET",
                payload: {
                    errorMessage: fullErrorMessage,
                },
            });

            throw new Error(`Error whitelisting tokens: ${fullErrorMessage}`);
        }
    };
